import { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  ArrowPathIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../../utils/axios';

// Level badge colors
const LEVEL_STYLES = {
  info: 'bg-green-100 text-green-700',
  warn: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
  audit: 'bg-blue-100 text-blue-700',
  debug: 'bg-gray-100 text-gray-600',
};

// Level icons
const LEVEL_ICONS = {
  info: InformationCircleIcon,
  warn: ExclamationTriangleIcon,
  error: XCircleIcon,
  audit: CheckCircleIcon,
  debug: InformationCircleIcon,
};

// Category colors
const CATEGORY_COLORS = {
  employee: 'bg-blue-50 text-blue-700 border-blue-200',
  security: 'bg-red-50 text-red-700 border-red-200',
  api: 'bg-purple-50 text-purple-700 border-purple-200',
  device: 'bg-green-50 text-green-700 border-green-200',
  ws: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  nginx: 'bg-amber-50 text-amber-700 border-amber-200',
  ssh: 'bg-pink-50 text-pink-700 border-pink-200',
  system: 'bg-gray-50 text-gray-700 border-gray-200',
};

// Source badge
function SourceBadge({ source }) {
  const colors = {
    mongodb: 'bg-green-100 text-green-700',
    websocket: 'bg-blue-100 text-blue-700',
    nginx: 'bg-amber-100 text-amber-700',
  };

  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${colors[source] || 'bg-gray-100 text-gray-600'}`}>
      {source}
    </span>
  );
}

// Event row component
function EventRow({ event, expanded, onToggle }) {
  const LevelIcon = LEVEL_ICONS[event.level] || InformationCircleIcon;
  const levelStyle = LEVEL_STYLES[event.level] || LEVEL_STYLES.info;
  const categoryColor = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.system;

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        onClick={onToggle}
        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
      >
        {/* Expand toggle */}
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />

        {/* Level icon */}
        <div className={`w-6 h-6 rounded flex items-center justify-center ${levelStyle}`}>
          <LevelIcon className="w-4 h-4" />
        </div>

        {/* Timestamp */}
        <div className="w-36 text-xs text-gray-500 font-mono">
          {new Date(event.timestamp).toLocaleString()}
        </div>

        {/* Category */}
        <span className={`text-xs px-2 py-0.5 rounded border ${categoryColor}`}>
          {event.category}
        </span>

        {/* Action */}
        <span className="text-sm font-medium text-gray-900 flex-1 truncate">
          {event.action}
        </span>

        {/* Outcome */}
        {event.outcome?.status && (
          <span className={`text-xs px-2 py-0.5 rounded ${
            event.outcome.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {event.outcome.status}
          </span>
        )}

        {/* Source */}
        <SourceBadge source={event.source} />
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-12 pb-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {/* Left column */}
            <div className="space-y-2">
              {event.message && (
                <div>
                  <span className="text-gray-500">Message:</span>
                  <p className="text-gray-900 mt-0.5">{event.message}</p>
                </div>
              )}
              {event.actor && (
                <div>
                  <span className="text-gray-500">Actor:</span>
                  <div className="mt-0.5 space-y-0.5">
                    {event.actor.email && <p className="text-gray-900">{event.actor.email}</p>}
                    {event.actor.ip && <p className="text-gray-600 font-mono text-xs">{event.actor.ip}</p>}
                    {event.actor.serial && <p className="text-gray-600 font-mono text-xs">Device: {event.actor.serial}</p>}
                  </div>
                </div>
              )}
              {event.target && (
                <div>
                  <span className="text-gray-500">Target:</span>
                  <div className="mt-0.5">
                    {event.target.type && <span className="text-xs text-gray-500">{event.target.type}: </span>}
                    <span className="text-gray-900">{event.target.id || event.target.name}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-2">
              {event.geo && (event.geo.city || event.geo.country) && (
                <div>
                  <span className="text-gray-500">Location:</span>
                  <p className="text-gray-900 mt-0.5">
                    {event.geo.city ? `${event.geo.city}, ` : ''}{event.geo.country}
                  </p>
                </div>
              )}
              {event.context?.reason && (
                <div>
                  <span className="text-gray-500">Reason:</span>
                  <p className="text-gray-900 mt-0.5">{event.context.reason}</p>
                </div>
              )}
              {event.duration !== undefined && (
                <div>
                  <span className="text-gray-500">Duration:</span>
                  <p className="text-gray-900 mt-0.5">{event.duration}ms</p>
                </div>
              )}
              {event.statusCode && (
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className="text-gray-900 mt-0.5">{event.statusCode}</p>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <span className="text-gray-500 text-xs">Additional Data:</span>
              <pre className="mt-1 text-xs bg-gray-100 rounded p-2 overflow-x-auto text-gray-700">
                {JSON.stringify(event.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Filter dropdown
function FilterDropdown({ label, value, options, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

export default function ActivityEvents({ entityType, entityId, timeRange = '7d' }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const limit = 20;

  // Filters
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('');
  const [outcome, setOutcome] = useState('');

  useEffect(() => {
    setPage(1);
  }, [entityType, entityId, timeRange, category, level, outcome]);

  useEffect(() => {
    fetchEvents();
  }, [entityType, entityId, timeRange, page, category, level, outcome]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        timeRange,
        page,
        limit,
      };
      if (category) params.category = category;
      if (level) params.level = level;
      if (outcome) params.outcome = outcome;

      const response = await axiosInstance.get(
        `/logs/activity/events/${entityType}/${encodeURIComponent(entityId)}`,
        { params }
      );

      setEvents(response.data.data.events || []);
      setTotalPages(response.data.data.pagination?.pages || 1);
      setTotalEvents(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.error?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (eventId) => {
    setExpandedId(expandedId === eventId ? null : eventId);
  };

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'employee', label: 'Employee' },
    { value: 'security', label: 'Security' },
    { value: 'api', label: 'API' },
    { value: 'device', label: 'Device' },
    { value: 'ws', label: 'WebSocket' },
    { value: 'nginx', label: 'Nginx' },
    { value: 'system', label: 'System' },
  ];

  const levelOptions = [
    { value: '', label: 'All Levels' },
    { value: 'info', label: 'Info' },
    { value: 'warn', label: 'Warning' },
    { value: 'error', label: 'Error' },
    { value: 'audit', label: 'Audit' },
  ];

  const outcomeOptions = [
    { value: '', label: 'All Outcomes' },
    { value: 'success', label: 'Success' },
    { value: 'failure', label: 'Failure' },
  ];

  if (loading && events.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="w-5 h-5 text-gray-400" />
          <h3 className="font-medium text-gray-900">Activity Events</h3>
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-gray-400" />
            <h3 className="font-medium text-gray-900">Activity Events</h3>
          </div>
          <button onClick={fetchEvents} className="text-blue-600 hover:text-blue-700 text-sm">
            Retry
          </button>
        </div>
        <div className="h-48 flex items-center justify-center text-gray-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-indigo-500" />
          <h3 className="font-medium text-gray-900">Activity Events</h3>
          <span className="text-sm text-gray-500">
            ({totalEvents.toLocaleString()} total)
          </span>
        </div>
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-4">
        <FunnelIcon className="w-4 h-4 text-gray-400" />
        <FilterDropdown
          label="Category"
          value={category}
          options={categoryOptions}
          onChange={setCategory}
        />
        <FilterDropdown
          label="Level"
          value={level}
          options={levelOptions}
          onChange={setLevel}
        />
        <FilterDropdown
          label="Outcome"
          value={outcome}
          options={outcomeOptions}
          onChange={setOutcome}
        />
      </div>

      {/* Events list */}
      <div className="max-h-[500px] overflow-y-auto">
        {events.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No events found matching filters
          </div>
        ) : (
          events.map((event, idx) => (
            <EventRow
              key={event._id || idx}
              event={event}
              expanded={expandedId === (event._id || idx)}
              onToggle={() => handleToggleExpand(event._id || idx)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="p-1.5 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="p-1.5 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
